import { ApolloError } from '@apollo/client'
import { errorHandler } from 'hooks/errorHandler'
import {
  useAsphaltPlacementMapImageLazyQuery,
  useConcretePlacementMapImageLazyQuery,
  useLotMapImageLazyQuery,
  useDailyInspectionControlPointMapImageLazyQuery,
  useDailyInspectionInspectionMapImageLazyQuery,
  useDailyInspectionPunchListMapImageLazyQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useCallback, useEffect, useState } from 'react'
import { LayerPreviewType } from '../../types'

export function useMapImageLazyQuery(
  typename: keyof typeof LayerPreviewType | undefined
) {
  const [src, _setSrc] = useState<string | null | undefined>()
  const [lastProjectLayerIds, setLastProjectLayerIds] = useState(
    Array<number>()
  )
  const [lastGlobalId, setLastGlobalId] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)
  const setSrc = (response?: { base64MapImage?: string | null }) => {
    _setSrc(response?.base64MapImage)
    setLoading(false)
  }
  /**
   * i'm using a custom error callback because we need to set loading as well
   */
  const onError = (err: ApolloError) => {
    _setSrc(undefined)
    setLoading(false)
    errorHandler(err)
  }
  const [asphalt] = useAsphaltPlacementMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onError,
    onCompleted: ({ asphaltPlacement }) => setSrc(asphaltPlacement),
  })
  const [concretePlacement] = useConcretePlacementMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onError,
    onCompleted: ({ concretePlacement }) => setSrc(concretePlacement),
  })
  const [lot] = useLotMapImageLazyQuery({
    ...DefaultQueryHookOptions,
    onError,
    onCompleted: ({ lot }) => setSrc(lot),
  })
  const [dailyInspectionControlPoint] =
    useDailyInspectionControlPointMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onError,
      onCompleted: ({ genericPlacements }) =>
        setSrc(genericPlacements.records.at(0)),
    })
  const [dailyInspectionInspection] =
    useDailyInspectionInspectionMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onError,
      onCompleted: ({ genericPlacements }) =>
        setSrc(genericPlacements.records.at(0)),
    })
  const [dailyInspectionPunchList] =
    useDailyInspectionPunchListMapImageLazyQuery({
      ...DefaultQueryHookOptions,
      onError,
      onCompleted: ({ genericPlacements }) =>
        setSrc(genericPlacements.records.at(0)),
    })
  useEffect(() => {
    setSrc(undefined)
  }, [typename])

  return {
    src,
    loading,
    lastProjectLayerIds,
    lastGlobalId,
    getMapImage: useCallback(
      (globalId: string, projectLayerIds: number[] = []) => {
        _setSrc(undefined)
        setLoading(true)
        setLastGlobalId(globalId)
        setLastProjectLayerIds(projectLayerIds)
        const options = { variables: { globalId, projectLayerIds } }
        if (typename === 'AsphaltPlacement') asphalt(options)
        else if (typename === 'ConcretePlacement') concretePlacement(options)
        else if (typename === 'DailyInspectionControlPoint')
          dailyInspectionControlPoint(options)
        else if (typename === 'DailyInspectionInspection')
          dailyInspectionInspection(options)
        else if (typename === 'DailyInspectionPunchList')
          dailyInspectionPunchList(options)
        else if (typename === 'Lot') lot(options)
      },
      [
        asphalt,
        concretePlacement,
        dailyInspectionControlPoint,
        dailyInspectionInspection,
        dailyInspectionPunchList,
        lot,
        typename,
      ]
    ),
  }
}
